import { JoistPlacement } from "building-types/floors/floorJoistPlacementTypes";
import { FloorFramingComponentType } from "building-types/framing/floorFramingComponentTypes";
import type { Framing } from "building-types/framing/floorFramingTypes";
import type { LumberPiecesByProfile, LumberRail } from "building-types/framing/lumberTypes";
import { polyLine2Move } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3ToPolyLine2IgnoreZ } from "geom/polyLine3";

export interface FloorFramingPolyline {
  readonly path: PolyLine2;
  readonly railId: string;
}

export interface FloorFramingPolylines {
  rims: FloorFramingPolyline[];
  joists: FloorFramingPolyline[];
  beams: FloorFramingPolyline[];
  noggins: FloorFramingPolyline[];
  headers: FloorFramingPolyline[];
  trimmers: FloorFramingPolyline[];
  tailJoists: FloorFramingPolyline[];
}

export function floorFramingPolylinesNew(): FloorFramingPolylines {
  return {
    rims: [],
    joists: [],
    beams: [],
    noggins: [],
    headers: [],
    trimmers: [],
    tailJoists: [],
  };
}

const framingComponentTypeToKey: Record<FloorFramingComponentType, keyof FloorFramingPolylines> = {
  [FloorFramingComponentType.RIM_JOIST]: "rims",
  [FloorFramingComponentType.FLOOR_JOIST]: "joists",
  [FloorFramingComponentType.FLOOR_BEAM]: "beams",
  [FloorFramingComponentType.NOGGIN]: "noggins",
  [FloorFramingComponentType.HEADER]: "headers",
  [FloorFramingComponentType.TRIMMER]: "trimmers",
  [FloorFramingComponentType.TAIL_JOIST]: "tailJoists",
};

function convertRailToPolyLine2(lumberRail: LumberRail, height: number): PolyLine2 {
  const line = polyLine3ToPolyLine2IgnoreZ(lumberRail.rail);
  polyLine2Move(line, lumberRail.nX * 0.5 * height, lumberRail.nY * 0.5 * height);
  return line;
}

export function collectPolylinesFromFramedFloorAssembly(framing: Framing[][], result: FloorFramingPolylines): void {
  for (const member of framing.flat()) {
    const { profileDescriptor, rails, type } = member;
    const { height } = profileDescriptor;
    const key = framingComponentTypeToKey[type];
    const target = result[key];
    for (const lumberRail of rails) {
      target.push({
        path: convertRailToPolyLine2(lumberRail, height),
        railId: lumberRail.id,
      });
    }
  }
}

const joistPlacementToKey: Record<JoistPlacement, keyof FloorFramingPolylines> = {
  [JoistPlacement.RIM]: "rims",
  [JoistPlacement.FLOOR]: "joists",
  [JoistPlacement.NOGGIN]: "noggins",
};

/**
 * Collects polylines from the legacy `SolidFramingFloor.floorFraming` path
 * (`LumberPiecesByProfile.railsByType` keyed by `JoistPlacement`).
 * Each rail is projected to 2D and offset by half the profile height along its normal.
 */
export function collectPolylinesFromFloorSystemFraming(
  joistPieces: LumberPiecesByProfile,
  result: FloorFramingPolylines,
): void {
  const { profileDescriptor, railsByType } = joistPieces;
  const { height } = profileDescriptor;

  for (const placement of Object.values(JoistPlacement)) {
    const rails = railsByType[placement];
    if (!rails) {
      continue;
    }
    const key = joistPlacementToKey[placement];
    const target = result[key];
    for (let j = 0, l = rails.length; j < l; j++) {
      const rail = rails[j];
      target.push({
        path: convertRailToPolyLine2(rail, height),
        railId: rail.id,
      });
    }
  }
}
